import React, { useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  message,
  Select,
  Tooltip,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { Tag } from "lucide-react";
import { DiscountMode } from "../../../Enums";
import { Product, Stock, PosAction } from "../../../Types";
import { useKeyboardShortcut } from "../../../Hook";
import { SHORTCUTS } from "../../../Constant";
import { useCart } from "../../../Context";
import { useSectionAccess } from "../../../Services";
import { modalTitle } from "../../../components";

interface SelectedProductProps {
  product: Product;
  selectedBatch: Stock | null;
  setSelectedBatch: (batch: Stock | null) => void;
  customPrice: number;
  setCustomPrice: (price: number) => void;
  customQty: number;
  setCustomQty: React.Dispatch<React.SetStateAction<number>>;
  priceInputRef: React.RefObject<any>;
  handleConfirmAdd: () => void;
  onDiscard: () => void;
  customCost: number;
  setCustomCost: (cost: number) => void;
  tempName: string;
  setTempName: (name: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  customDiscount: number;
  setCustomDiscount: (discount: number) => void;
  discountMode: DiscountMode;
  setDiscountMode: (mode: DiscountMode) => void;
}

export const SelectedProduct: React.FC<SelectedProductProps> = ({
  product,
  selectedBatch,
  setSelectedBatch,
  customPrice,
  setCustomPrice,
  customQty,
  setCustomQty,
  priceInputRef,
  handleConfirmAdd,
  onDiscard,
  customCost,
  setCustomCost,
  tempName,
  setTempName,
  description,
  setDescription,
  customDiscount,
  setCustomDiscount,
  discountMode,
  setDiscountMode,
}) => {
  const { isWholeSale } = useCart();
  const { isAllowed } = useSectionAccess("pos");
  const [editingName, setEditingName] = useState(false);
  const [showDescModal, setShowDescModal] = useState(false);

  useKeyboardShortcut(SHORTCUTS.POS.ADD_PRODUCT_TO_CART, (e) => {
    if (!product) return;
    e.preventDefault();
    e.stopPropagation();
    handleConfirmAdd();
  });

  const subtotal = customPrice * customQty;
  const discountAmount =
    discountMode === DiscountMode.PERCENT
      ? Math.min((subtotal * customDiscount) / 100, subtotal)
      : Math.min(customDiscount * customQty, subtotal);
  const netTotal = Math.max(0, subtotal - discountAmount);

  const handleDiscountChange = (val: number) => {
    setCustomDiscount(val || 0);
  };

  const handleAdd = () => {
    handleConfirmAdd();
  };

  return (
    <div className="flex items-start border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Image & Description Edit */}
      <div className="w-20 flex-shrink-0 bg-gray-50 flex flex-col items-center justify-between py-2.5 border-r border-gray-100 min-h-[100px] select-none">
        <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded bg-white border border-gray-200">
          {product.image_paths ? (
            <img
              src={product.image_paths}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-[10px] text-gray-400">No Img</span>
          )}
        </div>

        <Tooltip title={description ? description : "Add note/warranty"}>
          <Button
            type={description ? "primary" : "text"}
            size="small"
            ghost={!!description}
            icon={<EditOutlined style={{ fontSize: "10px" }} />}
            onClick={() => setShowDescModal(true)}
            className={`mt-1.5 flex items-center justify-center text-[10px] h-6 px-1.5 rounded-md ${
              description
                ? "border-indigo-500 text-indigo-600 bg-indigo-50/20 font-semibold"
                : "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50/50"
            }`}
          >
            <span>{description ? "Note set" : "Note"}</span>
          </Button>
        </Tooltip>
      </div>

      {/* Meta */}
      <div className="w-48 flex-shrink-0 px-3.5 py-3 border-r border-gray-100 flex flex-col justify-center gap-0.5">
        {product.is_tempory_item ? (
          editingName ? (
            <Input
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setEditingName(false);
                }
              }}
              size="small"
              autoFocus
              className="text-xs font-semibold mb-1.5"
            />
          ) : (
            <Tooltip title="Click to edit temporary item name">
              <p
                className="text-xs font-semibold text-gray-900 leading-snug mb-1.5 cursor-pointer hover:text-indigo-600 underline decoration-dotted"
                onClick={() => setEditingName(true)}
              >
                {tempName || product.title}
              </p>
            </Tooltip>
          )
        ) : (
          <p className="text-xs font-medium text-gray-900 leading-snug mb-1.5">
            {product.title}
          </p>
        )}
        <p className="text-xs my-0 py-0 text-gray-500">
          SKU: <span className="text-gray-800">{product.sku_code}</span>
        </p>
        <p className="text-xs my-0 py-0 text-gray-500">
          Available:{" "}
          <span className="text-gray-800">
            {selectedBatch?.quantity ?? 0} {product.unit}
          </span>
        </p>
        {selectedBatch && (
          <div className="flex flex-col text-xs my-0 py-0">
            <p className="text-xs my-0 py-0 text-gray-500">
              Cost:{" "}
              <span className="text-gray-800">
                {product.is_tempory_item
                  ? customCost
                  : selectedBatch?.cost_price != null
                    ? selectedBatch.cost_price
                    : "-"}
              </span>
            </p>
            <p className="text-xs my-0 py-0 text-gray-500">
              Retail:{" "}
              <span className="text-gray-800">
                Rs. {selectedBatch.retail_price?.toFixed(2)}
              </span>
            </p>
            <p className="text-xs my-0 py-0 text-gray-500">
              Wholesale:{" "}
              <span className="text-gray-800">
                Rs. {selectedBatch.wholesale_price?.toFixed(2)}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex-1 px-4 py-3 flex flex-col justify-center gap-2.5">
        {/* Batch / Cost */}
        {product?.is_tempory_item ? (
          isAllowed(PosAction.Cost) ? (
            <div className="flex items-center gap-2.5">
              <span className="text-sm text-gray-400 w-9 flex-shrink-0">
                Cost
              </span>
              <InputNumber
                value={customCost}
                onChange={(val) => setCustomCost(val || 0)}
                size="middle"
                min={0}
                precision={2}
                prefix="Rs."
                className="w-40 text-xs"
                keyboard={false}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="text-sm text-gray-400 w-9 flex-shrink-0">
                Cost
              </span>
              <span className="text-sm text-gray-800">
                LKR {customCost.toFixed(2)}
              </span>
            </div>
          )
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="text-[11px] text-gray-400 w-9 flex-shrink-0">
              Batch
            </span>
            <Select
              value={selectedBatch?.id}
              onChange={(val) => {
                const batch = product.stocks?.find((s) => s.id === val);
                if (batch) {
                  setSelectedBatch(batch);
                  setCustomPrice(
                    isWholeSale ? batch.wholesale_price : batch.retail_price,
                  );
                }
              }}
              size="small"
              className="flex-1 text-xs"
            >
              {product.stocks?.map((stock) => (
                <Select.Option key={stock.id} value={stock.id}>
                  {stock.batch_no} - {stock.quantity} {product.unit}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        {/* Price & Qty */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <span className="text-sm text-gray-400 w-9 flex-shrink-0">
              Price
            </span>
            {isAllowed(PosAction.PriceAdjustment) ? (
              <>
                <InputNumber
                  ref={priceInputRef}
                  value={customPrice}
                  onChange={(val) => setCustomPrice(val || 0)}
                  size="middle"
                  min={0}
                  precision={2}
                  prefix="Rs."
                  className="flex-1 text-xs"
                  keyboard={false}
                />
                <Tooltip
                  title={isWholeSale ? "Reset to wholesale" : "Reset to retail"}
                >
                  <Button
                    size="middle"
                    icon={<ReloadOutlined />}
                    onClick={() =>
                      selectedBatch &&
                      setCustomPrice(
                        isWholeSale
                          ? selectedBatch.wholesale_price
                          : selectedBatch.retail_price,
                      )
                    }
                  />
                </Tooltip>
              </>
            ) : (
              <span className="text-sm font-medium text-gray-800">
                LKR {customPrice.toFixed(2)}
              </span>
            )}
          </div>
          {/* Qty */}
          <div className="flex items-center gap-2.5">
            <span className="text-[11px] text-gray-400 w-9 flex-shrink-0">
              Qty
            </span>
            <div className="flex items-center border border-gray-200 rounded-md overflow-hidden bg-white">
              <Button
                size="middle"
                icon={<MinusOutlined />}
                className="border-none rounded-none bg-gray-50 hover:bg-gray-100 animate-none transition-none"
                onClick={() => setCustomQty((p) => Math.max(1, p - 1))}
              />
              <InputNumber
                value={customQty}
                onChange={(val) => setCustomQty(Math.max(1, val || 1))}
                size="middle"
                min={1}
                max={
                  product?.is_negative_qty_allowed === 1
                    ? undefined
                    : selectedBatch?.quantity
                }
                precision={0}
                controls={false}
                keyboard={false}
                className="w-12 text-center text-xs border-y-0 border-x border-gray-200 rounded-none qty-input-number"
                style={{ height: "32px", lineHeight: "32px" }}
              />
              <Button
                size="middle"
                icon={<PlusOutlined />}
                className="border-none rounded-none bg-gray-50 hover:bg-gray-100 animate-none transition-none"
                onClick={() => setCustomQty((p) => p + 1)}
              />
            </div>
          </div>
        </div>

        {/* Discount Row */}
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] text-gray-400 w-9 flex-shrink-0 flex items-center gap-0.5">
            <Tag className="w-3 h-3" />
            Disc
          </span>
          <div className="flex items-center gap-1.5 flex-1">
            <InputNumber
              value={customDiscount}
              onChange={(val) => handleDiscountChange(val || 0)}
              size="small"
              min={0}
              max={discountMode === DiscountMode.PERCENT ? 100 : customPrice}
              precision={2}
              prefix={discountMode === DiscountMode.AMOUNT ? "Rs." : undefined}
              suffix={discountMode === DiscountMode.PERCENT ? "%" : undefined}
              className="w-32 text-xs"
              keyboard={false}
              placeholder="0"
            />
            <Tooltip title="Toggle between fixed amount and percentage discount">
              <Button
                size="small"
                icon={<PercentageOutlined />}
                onClick={() => {
                  setDiscountMode(discountMode === DiscountMode.AMOUNT ? DiscountMode.PERCENT : DiscountMode.AMOUNT);
                  setCustomDiscount(0);
                }}
                className={`px-2 h-7 text-[10px] font-bold rounded-md transition-colors ${
                  discountMode === DiscountMode.PERCENT
                    ? "bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100"
                    : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
                }`}
              >
                {discountMode === DiscountMode.PERCENT ? "%" : "Rs."}
              </Button>
            </Tooltip>
            {discountAmount > 0 && (
              <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-md whitespace-nowrap border border-amber-200">
                -{discountAmount.toFixed(2)}
                {discountMode === DiscountMode.AMOUNT && customQty > 1 && (
                  <span className="ml-0.5 text-amber-400 font-normal">
                    ({customDiscount}×{customQty})
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Total + Actions */}
      <div className="w-36 flex-shrink-0 px-4 py-3 border-l border-gray-100 flex flex-col justify-between items-end">
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">
            Total
          </p>
          {discountAmount > 0 ? (
            <>
              <p className="text-xs text-gray-400 line-through">
                Rs. {subtotal.toFixed(2)}
              </p>
              <p className="text-lg font-semibold text-emerald-600">
                Rs. {netTotal.toFixed(2)}
              </p>
            </>
          ) : (
            <p className="text-lg font-medium text-gray-900">
              Rs. {subtotal.toFixed(2)}
            </p>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button
            type="primary"
            size="small"
            disabled={
              !product.is_tempory_item &&
              ((customQty > (selectedBatch?.quantity ?? 0) &&
                product.is_negative_qty_allowed !== 1) ||
                customPrice === 0)
            }
            onClick={handleAdd}
            className="bg-emerald-500 hover:bg-emerald-600 border-none px-4 text-xs"
          >
            Add
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onDiscard}
          />
        </div>
      </div>

      <Modal
        title={modalTitle("Item Note / Description")}
        open={showDescModal}
        onOk={() => setShowDescModal(false)}
        onCancel={() => setShowDescModal(false)}
        okText="Done"
        okButtonProps={{ size: "middle" }}
        cancelButtonProps={{ style: { display: "none" }, size: "middle" }}
        bodyStyle={{ paddingTop: "10px" }}
      >
        <Input.TextArea
          placeholder="Enter item description, warranty details, or serial numbers..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          autoFocus
        />
      </Modal>
    </div>
  );
};
